import {
  Component,
  Host,
  h,
  Prop,
  State,
  Watch,
  Element,
  Event,
  EventEmitter,
  Method
} from "@stencil/core";
import { MercuryBundles, getIconPath } from "@genexus/mercury";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/form",
  "utils/layout",
  "utils/typography",
  "components/button",
  "components/icon",
  "components/combo-box",
  "components/edit",
  "components/tabular-grid"
];

const CLOSE_ICON = getIconPath({
  category: "system",
  name: "close",
  colorType: "primary"
});
const PROGRESS_TRANSITION_SPEED = 200;

@Component({
  tag: "gx-ide-progress-bar",
  styleUrl: "progress-bar.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/progress-bar"]
})
export class GxIdeProgressBar {
  @Element() el: HTMLGxIdeProgressBarElement;

  /**
   * If true it will display the caption
   */
  @State() topStateBarVisible: boolean = false;

  /**
   * If true the progress will be updated instantly, without transition. This is required when the host user is calling the "reset" method.
   */
  @State() disableTransition: boolean = false;

  /**
   * If true it will display bar
   */
  @State() visible: boolean = false;

  /**
   * The top-bar title
   */
  @Prop() readonly caption: string;

  /**
   * It will only display the bar (no title, no close button)
   */
  @Prop({ reflect: true }) readonly minimal: boolean = false;

  /**
   * The top-bar active state. If false it will be hidden
   */
  @Prop({ mutable: true }) active: boolean = false;
  @Watch("active")
  watchActiveHandler(active: boolean) {
    if (active) {
      this.visible = true;
      setTimeout(() => {
        this.topStateBarVisible = true;
      }, 300);
    } else {
      this.topStateBarVisible = false;
      setTimeout(() => {
        this.visible = false;
        this.el.style.setProperty("--top-bar-progress", `0%`);
        this.progressBarClosed.emit();
      }, 300);
    }
  }

  /**
   * The top-bar state
   */
  @Prop({ mutable: true }) stateType: TopStateBarType = "in-progress";

  /**
   * The progress bar progress
   */
  @Prop({ mutable: true }) progress: number = undefined;
  @Watch("progress")
  watchProgressHandler(progress: number) {
    if (progress >= 0 && progress <= 100) {
      this.el.style.setProperty("--top-bar-progress", `${progress}%`);
    }
    if (progress === 100 && this.closeType === "auto-close") {
      setTimeout(() => {
        this.active = false;
      }, 200);
    }
  }

  /**
   * How to display the close button
   */
  @Prop() readonly closeType: "auto-close" | "button" = "button";

  /**
   * A callback that gets called when the top-state-bar is closed
   */
  @Prop() readonly closedCallback: () => void = () => {};

  /**
   * Emitted every time the progress bar is closed.
   */
  @Event() progressBarClosed: EventEmitter;

  componentWillLoad() {
    this.evaluateInitialProgress();
    if (this.active) {
      this.visible = true;
      this.topStateBarVisible = true;
    }
  }

  /**
   * Sets the progress bar to zero instantly (no transition) and changes the type
   * to 'accent'. Useful when reusing the progress bar for a new process.
   */
  @Method()
  async reset() {
    // Resetting involves setting the progress to zero. In this case it is preferable
    // to disable the transition.
    this.disableTransition = true;
    this.progress = 0;
    this.stateType = "accent";
    setTimeout(() => {
      this.disableTransition = false; // enable transition for the nex process.
    }, PROGRESS_TRANSITION_SPEED);
  }

  private evaluateInitialProgress = () => {
    if (
      this.progress === undefined &&
      (this.stateType === "error" ||
        this.stateType === "warning" ||
        this.stateType === "success")
    ) {
      this.progress = 100;
    } else if (
      this.progress === undefined &&
      this.stateType === "in-progress"
    ) {
      this.progress = 0;
    }
    this.el.style.setProperty("--top-bar-progress", `${this.progress}%`);
  };

  private closeClickHandler = () => {
    this.active = false;
    this.closedCallback();
  };

  private renderClose = () => {
    return (
      this.closeType === "button" && (
        <button
          class="button-tertiary button-icon-only"
          aria-label="close"
          onClick={this.closeClickHandler}
          role="button"
          tabIndex={this.visible ? 0 : -1}
        >
          <ch-image src={CLOSE_ICON} class="icon-m"></ch-image>
        </button>
      )
    );
  };

  render() {
    return (
      <Host
        class={{
          "visible": this.visible,
          "with-close-button": this.closeType === "button",
          "disable-transition": this.disableTransition
        }}
        aria-hidden={!this.active}
        style={{
          "--progress-bar-transition-speed": `${PROGRESS_TRANSITION_SPEED}ms`
        }}
      >
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <div
          role="status"
          aria-labelledby="label"
          aria-hidden={!this.active}
          class={{
            "top-state-bar": true,
            [`top-state-bar--${this.stateType}`]: true,
            "top-state-bar--visible": this.topStateBarVisible
          }}
        >
          <div class="top-state-bar__outer-wrapper">
            {!this.minimal ? (
              <div class="top-state-bar__inner-wrapper">
                <label
                  id="label"
                  class={{
                    "top-state-bar__caption": true
                  }}
                  part="label"
                >
                  {this.caption}
                </label>

                {this.renderClose()}
              </div>
            ) : null}
            <div
              class={{
                "top-state-bar__progress-wrapper": true
              }}
              part="progress-wrapper"
            >
              <span class="progress-bar"></span>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}

export type TopStateBarType =
  | "accent"
  | "success"
  | "warning"
  | "error"
  | "in-progress";
